import { useMemo } from 'react';
import { useGetVenteQuery } from 'actions/ventes/queries';
import { useGetClientQuery } from 'actions/clients/queries';
import { useUpdateVenteMutation } from 'actions/ventes/mutations';
import { useGetEcrituresManuellesQuery } from 'actions/comptabilite/queries';
import { toast } from 'react-toastify';
import { extractListData, formatQueryError } from 'utils/queryHelpers';

const useVentesData = (dossierId) => {
  const { data: venteResponse, error: venteError, isLoading: isLoadingVentes, isFetching: isFetchingVentes, refetch } = useGetVenteQuery({
    dossier_id: dossierId
  });
  const { data: clientResponse, error: clientError, isLoading: isLoadingClients } =
    useGetClientQuery({ dossier_id: dossierId });
  const { data: ecrituresResponse, refetch: refetchEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );

  const [updateVente] = useUpdateVenteMutation();

  const updatedVentes = useMemo(() => {
    const ventesData = extractListData(venteResponse, venteError, isLoadingVentes);
    const clientData = extractListData(clientResponse, clientError, isLoadingClients);

    const clientMap = clientData.reduce((acc, client) => {
      acc[client.id] = client.nom;
      return acc;
    }, {});

    // Map compte_comptable -> nom client pour les écritures manuelles
    const clientByCompte = {};
    clientData.forEach((c) => {
      if (c.compte_comptable) clientByCompte[String(c.compte_comptable)] = c;
    });

    const ventes = ventesData.map((vente) => {
      const clientNom = (vente.client_details?.nom) || clientMap[vente.clientId] || clientMap[vente.client] || vente.client_nom || 'Non spécifié';
      return {
        ...vente,
        client_details: vente.client_details?.nom
          ? vente.client_details
          : { nom: clientNom },
        clientNom: clientNom,
        client_nom: clientNom,
      };
    });

    // IDs d'écritures déjà synchronisées sous forme de vraies Entrées en base de données
    const syncedEcritureIds = new Set(
      ventesData
        .map((v) => {
          if (!v.ecriture_comptable) return null;
          return typeof v.ecriture_comptable === 'object' ? v.ecriture_comptable.id : v.ecriture_comptable;
        })
        .filter(Boolean)
    );

    // ── Écritures manuelles non liées à une Entree → affichées comme ventes ──
    const ecritures = ecrituresResponse?.ecritures || [];
    const ecrituresAsVentes = ecritures
      .filter((e) => {
        // Seules les écritures explicitement manuelles ou de régularisation peuvent être considérées
        if (!e.est_manuelle && !e.est_regularisation && e.source_origine !== 'MANUELLE' && e.source_origine !== 'REGULARISATION') return false;
        // Si l'écriture est liée à une sortie/entrée existante ou déjà synchronisée en base, ne pas doubler
        if (e.sortie_source || e.entree_source) return false;
        if (syncedEcritureIds.has(e.id)) return false;
        const codeDebit = String(e.code_debit || "");
        const codeCredit = String(e.code_credit || "");
        return codeDebit.startsWith("411") || codeCredit.startsWith("7") || (codeCredit.startsWith("411") && codeDebit.startsWith("5"));
      })
      .map((e) => {
        const codeDebit = String(e.code_debit || "");
        const client = codeDebit.startsWith("411") ? clientByCompte[codeDebit] : null;
        const montant = Number(e.debit || e.credit || 0);
        const cNom = client ? client.nom : (codeDebit.startsWith("411") ? "Client (compte " + codeDebit + ")" : "Non spécifié");
        return {
          id: `MAN-${e.id}`,
          _source: "manuelle",
          intitule: e.libelle_original || e.libelle || "Écriture manuelle",
          client_details: { nom: cNom },
          clientNom: cNom,
          client_nom: cNom,
          created_by_details: { first_name: "Saisie manuelle" },
          date_transaction: e.date || null,
          created_at: e.date || null,
          updated_at: e.date || null,
          statut_paiement: e.workflow_status === "VALIDE" ? "PAYE" : "NON_PAYE",
          prix: montant,
          montant_total: montant,
          montant_paye: e.workflow_status === "VALIDE" ? montant : 0,
          reste_a_payer: e.workflow_status === "VALIDE" ? 0 : montant,
          taxes: 0,
          piece_joint: null,
          produits_associes: [],
          archived: false,
        };
      });

    // TRI UNIFIÉ : Toujours afficher le plus récent en premier (date_transaction DESC, puis ID DESC)
    const all = [...ventes, ...ecrituresAsVentes];
    all.sort((a, b) => {
      const dateA = new Date(a.date_transaction || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.date_transaction || b.created_at || b.updated_at || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      const idA = typeof a.id === 'number' ? a.id : parseInt(String(a.id).replace(/\D/g, '') || '0', 10);
      const idB = typeof b.id === 'number' ? b.id : parseInt(String(b.id).replace(/\D/g, '') || '0', 10);
      return idB - idA;
    });

    return all;
  }, [venteResponse, clientResponse, ecrituresResponse, venteError, clientError, isLoadingVentes, isLoadingClients]);

  const queryError = venteError ? formatQueryError(venteError) : (clientError ? formatQueryError(clientError) : null);

  const handleUpdateVente = async (venteId, updateData) => {
    try {
      await updateVente({ id: venteId, ...updateData }).unwrap();
      await refetch();
      return true;
    } catch (error) {
      toast.error(
        `Erreur lors de la mise à jour: ${error.data?.error || error.message}`
      );
      console.error(error);
      return false;
    }
  };

  return {
    updatedVentes,
    handleUpdateVente,
    refetch: () => { refetch(); refetchEcritures(); },
    error: queryError,
    isLoading: isLoadingVentes || (isFetchingVentes && (!venteResponse || extractListData(venteResponse).length === 0)),
  };
};

export default useVentesData;

